import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaChevronLeft,
  FaChevronRight,
  FaHtml5,
  FaCss3Alt,
  FaReact,
  FaLeaf,
} from "react-icons/fa";
import {
  SiTypescript,
  SiRedux,
  SiTailwindcss,
  SiJsonwebtokens,
} from "react-icons/si";

import rr1 from "../assets/rr1.jpeg";
import rr2 from "../assets/rr2.jpeg";
import rr3 from "../assets/rr3.jpeg";
import ww1 from "../assets/ww1.jpeg";
import ww2 from "../assets/ww2.jpeg";
import ww3 from "../assets/ww3.jpeg";
import c1 from "../assets/c1.jpeg";
import c2 from "../assets/c2.jpeg";
import c3 from "../assets/c3.jpeg";
import e1 from "../assets/e1.png";
import e2 from "../assets/e2.png";
import e3 from "../assets/e3.png";

const projectData = [
  {
    title: "RouteRover",
    description:
      "Built a travel itinerary planning app featuring user registration, interactive map integration for route visualization, and a dashboard for itinerary management and trip tracking across devices.",
    images: [rr1, rr2, rr3],
    techStack: [
      "HTML",
      "TailwindCSS",
      "TypeScript",
      "React",
      "Leaflet",
      "JSON server",
    ],
    githubLink: "https://github.com/hossainsmshakib/RouteRover",
  },
  {
    title: "WealthWatch",
    description:
      "Developed a personal finance tracking app with user registration, transaction management, monthly budgeting, and data visualization across devices.",
    images: [ww1, ww2, ww3],
    techStack: [
      "HTML",
      "TailwindCSS",
      "React",
      "Redux",
      "TypeScript",
      "JSON server",
    ],
    githubLink: "https://github.com/hossainsmshakib/WealthWatch",
  },
  {
    title: "Culinary",
    description:
      "Developed a recipe management app with user registration, recipe creation and management, search and filter options, ratings and reviews, and a responsive admin panel.",
    images: [c1, c2, c3],
    techStack: [
      "HTML",
      "TailwindCSS",
      "React",
      "Redux",
      "TypeScript",
      "JSON server",
    ],
    githubLink: "https://github.com/hossainsmshakib/Culinary",
  },
  {
    title: "Eventora",
    description:
      "A responsive app for discovering local events by location, date, and category. It offers event search, filters, bookmarking, interactive maps, and directions.",
    images: [e1, e2, e3],
    techStack: [
      "HTML",
      "TailwindCSS",
      "React",
      "Redux",
      "TypeScript",
      "JSON server",
    ],
    githubLink: "https://github.com/hossainsmshakib/Eventora",
  },
];

const techStackIcons: { [key: string]: JSX.Element } = {
  HTML: <FaHtml5 className="text-orange-500" />,
  TailwindCSS: <SiTailwindcss className="text-cyan-400" />,
  TypeScript: <SiTypescript className="text-blue-500" />,
  React: <FaReact className="text-blue-400" />,
  Redux: <SiRedux className="text-purple-500" />,
  Leaflet: <FaLeaf className="text-green-500" />,
  "JSON server": <SiJsonwebtokens className="text-gray-400" />,
};

const Projects: React.FC = () => {
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);

  const handleNextProject = () => {
    setCurrentProjectIndex((prevIndex) => (prevIndex + 1) % projectData.length);
  };

  const handlePrevProject = () => {
    setCurrentProjectIndex(
      (prevIndex) => (prevIndex - 1 + projectData.length) % projectData.length
    );
  };

  return (
    <section id="projects" className="py-20 bg-[#111827]">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold mb-12 text-center text-teal-400"
        >
          My Projects
        </motion.h2>
        <div className="relative">
          <div className="flex justify-center">
            <ProjectCard project={projectData[currentProjectIndex]} />
          </div>
          <button
            onClick={handlePrevProject}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-teal-400 text-gray-900 rounded-full p-3 hover:bg-teal-300 transition duration-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
          >
            <FaChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleNextProject}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-teal-400 text-gray-900 rounded-full p-3 hover:bg-teal-300 transition duration-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
          >
            <FaChevronRight className="w-6 h-6" />
          </button>
        </div>
        <div className="flex justify-center mt-8">
          {projectData.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 mx-1 rounded-full transition-all duration-300 ${
                currentProjectIndex === index
                  ? "bg-teal-400 scale-125"
                  : "bg-gray-500"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectCard: React.FC<{ project: any }> = ({ project }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleNextImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % project.images.length
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prevIndex) =>
        (prevIndex - 1 + project.images.length) % project.images.length
    );
  };

  return (
    <motion.div
      className="bg-gray-800 rounded-lg shadow-lg overflow-hidden transition duration-300 hover:shadow-2xl w-full max-w-4xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col md:flex-row h-[600px]">
        <div className="relative w-full md:w-1/2 h-64 md:h-full">
          <motion.img
            src={project.images[currentImageIndex]}
            alt={`Project ${project.title} Image`}
            className="w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          <div className="absolute inset-0 flex items-center justify-between p-2">
            <button
              onClick={handlePrevImage}
              className="bg-teal-400 text-gray-900 rounded-full p-2 hover:bg-teal-300 transition duration-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextImage}
              className="bg-teal-400 text-gray-900 rounded-full p-2 hover:bg-teal-300 transition duration-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="w-full md:w-1/2 p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-2xl font-semibold mb-2 text-teal-400">
              {project.title}
            </h3>
            <p className="text-gray-400 mb-4 text-sm md:text-base h-32 overflow-y-auto">
              {project.description}
            </p>
            <div className="mb-4">
              <strong className="text-teal-400">Tech Stack:</strong>
              <div className="flex flex-wrap gap-2 mt-2">
                {project.techStack.map((tech: string, index: number) => (
                  <div
                    key={index}
                    className="flex items-center bg-gray-700 rounded-full px-3 py-1"
                  >
                    {techStackIcons[tech]}
                    <span className="ml-2 text-sm">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <a
            href={project.githubLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-teal-400 hover:text-teal-300 transition duration-300 mt-4 text-sm"
          >
            <FaGithub className="mr-2" />
            Source Code
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default Projects;
